import { useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { TextInputLight } from 'components/site/layouts/DefaultPageLayout/TextInputLight';
import { S_WebButtonSmaller } from 'elements/button/WebButton';
import { motion } from 'framer-motion';
import {
    RegisterUserByEmail,
    RegisterUserByEmailVariables
} from 'libs/graphql/mutation/access/__generated__/RegisterUserByEmail';
import { REGISTER_USER_MUTATION } from 'libs/graphql/mutation/access/accessMutation';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EColors, EFonts, EShadows } from 'libs/styles/styling';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { VALID_EMAIL_HASH } from 'libs/utils/validation/validation';
import { useState } from 'react';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    onSent: (email: string) => void;
    className?: string;
}

export const EmailRegister = (props: Props): JSX.Element => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [sent, setSent] = useState(false);
    const [agree, setAgree] = useState<boolean>(false);
    const [processing, setProcessing] = useState(false);

    const canSubmit =
        email && password && confirmPassword && !!email.match(VALID_EMAIL_HASH) && agree;

    const [submit] = useMutation<RegisterUserByEmail, RegisterUserByEmailVariables>(
        REGISTER_USER_MUTATION,
        {
            variables: {
                email,
                password,
                confirmPassword
            },
            onCompleted: () => {
                setSent(true);
                setProcessing(false);
                props.onSent(email);
            },
            onError: (err) => {
                const r = err.message;
                if (r === 'INVALID_EMAIL') {
                    userMessage(EToastType.ERROR, Localization.INVALID_EMAIL);
                } else if (r === 'PASSWORD_NOT_VALID') {
                    userMessage(EToastType.ERROR, Localization.PASSWORD_NOT_STRONG);
                } else if (r === 'EMAIL_ALREADY_REGISTERED') {
                    userMessage(EToastType.ERROR, Localization.EMAIL_USED);
                } else {
                    userMessage(EToastType.ERROR, Localization.ERROR_REGISTRATION);
                }
                setProcessing(false);
            }
        }
    );

    return (
        <S_EmailRegister className={props.className}>
            {!sent && (
                <form
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (canSubmit) {
                                submit();
                            }
                        }
                    }}
                >
                    <S_TextInputLight
                        value={email}
                        label={Localization.EMAIL}
                        autoComplete="email"
                        onChange={(value) => {
                            setEmail(value);
                        }}
                    />

                    <S_TextInputLight
                        value={password}
                        label={Localization.PASSWORD}
                        onChange={(value) => {
                            setPassword(value);
                        }}
                        type="password"
                    />

                    <S_TextInputLight
                        value={confirmPassword}
                        label={Localization.CONFIRM_PASSWORD}
                        onChange={(value) => {
                            setConfirmPassword(value);
                        }}
                        type="password"
                    />
                    <S_Agree>
                        <motion.div {...conditionalScaleAnimation(true, 1.1, 1)}>
                            <S_Check onClick={() => setAgree(!agree)}>
                                {agree && <S_Checked icon={['fas', 'circle-check']} />}
                            </S_Check>
                        </motion.div>
                        <S_AgreeText onClick={() => setAgree(!agree)}>
                            {Localization.AGREE_WITH}{' '}
                            <a href="/terms" target="_blank">
                                {Localization.TERMS_AND_CONDITIONS}
                            </a>
                        </S_AgreeText>
                    </S_Agree>
                    <S_Center>
                        <S_WebButton
                            text={Localization.REGISTER_ACTION}
                            type="primary"
                            shadow={EShadows.LIGHT}
                            onClick={() => {
                                setProcessing(true);
                                submit();
                            }}
                            loading={processing}
                            disabled={!canSubmit}
                        />
                    </S_Center>
                </form>
            )}
        </S_EmailRegister>
    );
};

const S_EmailRegister = styled.div``;

const S_TextInputLight = styled(TextInputLight)`
    margin-bottom: 20px;
`;

const S_Center = styled.div`
    display: flex;
    justify-content: center;
`;

const S_WebButton = styled(S_WebButtonSmaller)`
    margin: 0 0 15px 0;
`;

const S_Check = styled.div`
    margin: 0 15px 0 0;
    width: 28px;
    height: 28px;
    cursor: pointer;
    border-radius: 50%;
    border: 1px solid #8286a2;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: white;
    flex-shrink: 0;
`;

const S_Checked = styled(FontAwesomeIcon)`
    font-size: 23px;
    color: ${EColors.DARK};
`;

const S_Agree = styled.div`
    display: flex;
    align-items: center;
    margin: 25px 0 25px 0;
`;

const S_AgreeText = styled.div`
    color: ${EColors.DARK};
    font-size: 15px;
    font-family: ${EFonts.Regular};
    white-space: nowrap;
    a {
        color: ${EColors.DARK};
        font-family: ${EFonts.Regular};
        text-decoration: underline;
        &:hover {
            text-decoration: none;
        }
    }
`;
